import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FilmGrain } from "@/components/effects/FilmGrain";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServicePage from "@/pages/ServicePage";
import Solutions from "@/pages/Solutions";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import IkkeAlene from "@/pages/IkkeAlene";
import { IKKE_ALENE } from "@/data/content";

const SEO = {
  "/": { en: "Jorviko — Software, AI & Automation for Businesses in Norway", no: "Jorviko — Programvare, AI og automatisering for bedrifter i Norge" },
  "/services": { en: "Services — Software Development & AI Automation Norway | Jorviko", no: "Tjenester — Programvareutvikling og AI-automatisering Norge | Jorviko" },
  "/services/software-development": { en: "Custom Software Development Norway | Jorviko", no: "Skreddersydd programvareutvikling Norge | Jorviko" },
  "/services/ai-automation": { en: "AI Development & Business Automation Norway | Jorviko", no: "AI-utvikling og forretningsautomatisering Norge | Jorviko" },
  "/services/web-development": { en: "Web Application Development Norway | Jorviko", no: "Utvikling av webapplikasjoner Norge | Jorviko" },
  "/services/cloud-modernisation": { en: "Azure Development & Cloud Solutions Norway | Jorviko", no: "Azure-utvikling og skyløsninger Norge | Jorviko" },
  "/solutions": { en: "Example Solutions — Software & AI for Real Businesses | Jorviko", no: "Eksempelløsninger — Programvare og AI for virksomheter | Jorviko" },
  "/about": { en: "About Jorviko — Software Company in Norway", no: "Om Jorviko — Programvareselskap i Norge" },
  "/contact": { en: "Contact Jorviko — Discuss Your Project", no: "Kontakt Jorviko — Diskuter ditt prosjekt" },
  "/ikke-alene": IKKE_ALENE.seo.title,
};

const DEFAULT_DESC = {
  en: "Jorviko is a Norwegian software and AI company. We build custom software, websites, AI automation and cloud solutions for businesses across Norway.",
  no: "Jorviko er et norsk programvare- og AI-selskap. Vi bygger skreddersydd programvare, nettsider, AI-automatisering og skyløsninger for bedrifter i Norge.",
};

const DESC = {
  "/services": {
    en: "Custom software, AI & automation, web development and Azure cloud modernisation for Norwegian businesses.",
    no: "Skreddersydd programvare, AI og automatisering, webutvikling og Azure-skymodernisering for norske bedrifter.",
  },
  "/services/software-development": {
    en: "Custom software development in Norway. SaaS products, internal systems, customer portals, dashboards and legacy replacement built around how your business works.",
    no: "Skreddersydd programvareutvikling i Norge. SaaS-produkter, interne systemer, kundeportaler, dashbord og utskifting av gamle systemer bygget rundt din virksomhet.",
  },
  "/services/ai-automation": {
    en: "AI automation in Norway. AI assistants, document search, workflow automation and RAG systems that remove repetitive work.",
    no: "AI-automatisering i Norge. AI-assistenter, dokumentsøk, arbeidsflytautomatisering og RAG-systemer som fjerner repetitivt arbeid.",
  },
  "/services/web-development": {
    en: "Web application development in Norway. Business websites, customer portals, booking systems and admin tools.",
    no: "Utvikling av webapplikasjoner i Norge. Bedriftsnettsider, kundeportaler, bookingsystemer og administrasjonsverktøy.",
  },
  "/services/cloud-modernisation": {
    en: "Azure development and cloud solutions in Norway. Modernise legacy systems, connect applications and scale securely.",
    no: "Azure-utvikling og skyløsninger i Norge. Moderniser gamle systemer, koble sammen applikasjoner og skaler sikkert.",
  },
  "/solutions": {
    en: "Example solutions showing how custom software, AI and automation solve everyday problems — from inventory and project management to client portals and reporting.",
    no: "Eksempelløsninger som viser hvordan skreddersydd programvare, AI og automatisering løser dagligdagse problemer — fra lagerstyring og prosjektstyring til kundeportaler og rapportering.",
  },
  "/about": {
    en: "Jorviko is a Norwegian software and AI company focused on turning real business problems into useful technology.",
    no: "Jorviko er et norsk programvare- og AI-selskap som fokuserer på å gjøre reelle forretningsproblemer om til nyttig teknologi.",
  },
  "/ikke-alene": IKKE_ALENE.seo.desc,
};

function RouteEffects({ lenis }) {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    if (lenis?.current) lenis.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, lenis]);

  useEffect(() => {
    const title = (SEO[pathname] || SEO["/"])[lang];
    document.title = title;
    document.documentElement.lang = lang === "no" ? "nb" : "en";
    const desc = (DESC[pathname] || DEFAULT_DESC)[lang];

    const setMeta = (selector, attr, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/);
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('meta[name="description"]', "name", desc);
    setMeta('meta[property="og:title"]', "property", title);
    setMeta('meta[property="og:description"]', "property", desc);
  }, [pathname, lang]);

  return null;
}

function Shell() {
  const lenis = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const l = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
    lenis.current = l;
    let raf;
    const loop = (time) => { l.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); l.destroy(); lenis.current = null; };
  }, []);

  return (
    <>
      <FilmGrain />
      <RouteEffects lenis={lenis} />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/ikke-alene" element={<IkkeAlene />} />
        </Routes>
      </main>
      <Footer />
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0A0E17", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } }} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
